// ============================================================================
// FacilityCombobox — searchable input replacing the 176-option <select> on
// AM Co-pilot proposal cards. Vanilla React (no shadcn Command set in repo),
// keyboard-navigable, click-outside closes.
//
// Used by FeedbackCard, TaskCard, SupplyOrderCard. Same FacilityOpt shape
// the three cards already accept.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FacilityOpt {
  facility_id: string;
  facility_name: string;
}

interface FacilityComboboxProps {
  value: string;
  options: FacilityOpt[];
  onChange: (facility_id: string) => void;
  placeholder?: string;
}

export function FacilityCombobox({
  value,
  options,
  onChange,
  placeholder = 'Select a facility…',
}: FacilityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.facility_id === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.facility_name.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Click-outside closes
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Keep active option in view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(
      `li[data-idx="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  // Reset active index when filter changes
  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  function handleOpen() {
    setOpen(true);
    setQuery('');
    // Focus search input on next paint
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[activeIdx];
      if (pick) {
        onChange(pick.facility_id);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      {!open ? (
        <button
          type="button"
          onClick={handleOpen}
          className="flex w-full items-center justify-between rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-left text-sm text-tcs-text hover:border-tcs-border-input focus:border-tcs-text-muted focus:outline-none focus:ring-1 focus:ring-tcs-text-muted"
        >
          <span className={selected ? '' : 'text-tcs-text-muted'}>
            {selected ? selected.facility_name : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 text-tcs-text-muted" />
        </button>
      ) : (
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type to search facilities…"
          className="w-full rounded-md border border-tcs-text-muted bg-tcs-field px-2 py-1.5 text-sm text-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text-muted"
        />
      )}

      {open && (
        <ul
          ref={listRef}
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-tcs-border bg-tcs-overlay text-sm shadow-lg"
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="px-2 py-2 text-tcs-text-muted">No matches</li>
          ) : (
            filtered.map((opt, idx) => (
              <li
                key={opt.facility_id}
                data-idx={idx}
                role="option"
                aria-selected={idx === activeIdx}
                onMouseDown={(e) => {
                  // mousedown to beat the click-outside listener
                  e.preventDefault();
                  onChange(opt.facility_id);
                  setOpen(false);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`cursor-pointer px-2 py-1.5 ${
                  idx === activeIdx
                    ? 'bg-tcs-inverse text-tcs-inverse-ink'
                    : 'text-tcs-text hover:bg-tcs-surface'
                }`}
              >
                {opt.facility_name}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
